import { useState, useCallback, createElement, type ReactNode } from "react";
import type { DayProps } from "react-day-picker";
import { asLocalDate } from "@freightsimple/time/branded-types";
import {
  fromJsDate,
  toJsDate,
  today,
  isLocalDateInThePast,
} from "@freightsimple/time/helpers";
import type { LocalDate } from "@freightsimple/time/branded-types";
import { useMarkDialogDirty } from "../../Dialog/FormDirtyContext";
import { Tooltip, DENSE_GRID_TOOLTIP_DELAY_MS } from "../../Feedback/Tooltip";
import { Calendar } from "../Internal/Calendar";

interface InlineCalendarProps {
  value?: LocalDate | undefined;
  onChange?: ((date: LocalDate | undefined) => void) | undefined;
  disabledDate?: ((date: LocalDate) => boolean) | undefined;
  /**
   * Grey out and prevent selection of dates before today. Composes with
   * disabledDate. Also opens the calendar on today's month when `value` falls
   * in an earlier one.
   */
  disablePastDates?: boolean | undefined;
  /**
   * Short reason shown in a tooltip when hovering a greyed-out day, explaining
   * why it can't be picked (e.g. "Must be on or after the date"). Same message
   * for every disabled day; callers make it specific to the active restriction.
   */
  disabledHint?: ReactNode | undefined;
  /**
   * Stretch the grid to fill the container width with larger day buttons, and
   * show a "Jump to current month" button below the grid when the user has
   * navigated away from the current month.
   */
  fullWidth?: boolean | undefined;
  /**
   * Swap the "November 2025" caption for month and year dropdowns. Turn it on
   * for dates that sit years from today — a join date, a birthday — where
   * arrowing month by month is unusable.
   */
  yearNavigation?: boolean | undefined;
  /** First year the dropdown offers. Only used with `yearNavigation`. */
  startYear?: number | undefined;
  /** Last year the dropdown offers. Only used with `yearNavigation`. */
  endYear?: number | undefined;
}

// Wide enough that neither a birthday nor a join date needs the caller to think
// about it, while still keeping the year list scrollable rather than endless.
const DEFAULT_YEARS_BACK = 100;
const DEFAULT_YEARS_FORWARD = 10;

/**
 * An always-visible single-date calendar bound to a LocalDate. Use it when the
 * calendar should sit inline — in a dialog body or a panel — so a date is one
 * click away, with no trigger field or popover to open first. DatePicker
 * composes this inside its popover; reach for DatePicker when you want a
 * compact field-style input instead.
 */
export function InlineCalendar({
  value,
  onChange,
  disabledDate,
  disablePastDates = false,
  disabledHint,
  fullWidth = false,
  yearNavigation = false,
  startYear,
  endYear,
}: InlineCalendarProps) {
  const markDirty = useMarkDialogDirty();
  // With past dates disabled, a value in an earlier month would open on a page
  // where nothing can be picked, so open on today's month instead.
  const [month, setMonth] = useState<Date>(() =>
    value && !(disablePastDates && value.slice(0, 7) < today().slice(0, 7))
      ? toJsDate(value)
      : toJsDate(today()),
  );

  const isDateDisabled = useCallback(
    (date: LocalDate) =>
      (disablePastDates && isLocalDateInThePast(date)) ||
      (disabledDate?.(date) ?? false),
    [disablePastDates, disabledDate],
  );

  const hasDisabledDates = disablePastDates || !!disabledDate;
  const selected = value ? toJsDate(value) : undefined;

  function handleSelect(date: Date | undefined): void {
    if (!date) return;
    const iso = fromJsDate(date);
    if (isDateDisabled(iso)) return;
    markDirty?.();
    onChange?.(iso);
  }

  // Compare year-month prefixes ("YYYY-MM") so navigating within the current
  // month never surfaces the jump affordance.
  const offCurrentMonth = fromJsDate(month).slice(0, 7) !== today().slice(0, 7);

  // The dropdowns need explicit bounds, so turn the year numbers into the first
  // and last selectable month.
  const currentYear = Number(today().slice(0, 4));
  const firstYear = startYear ?? currentYear - DEFAULT_YEARS_BACK;
  const lastYear = endYear ?? currentYear + DEFAULT_YEARS_FORWARD;
  const yearBounds = yearNavigation
    ? {
        startMonth: toJsDate(asLocalDate(`${firstYear}-01-01`)),
        endMonth: toJsDate(asLocalDate(`${lastYear}-12-01`)),
      }
    : {};

  // A day cell whose button is wrapped in a tooltip explaining why it's
  // disabled. The tooltip sits INSIDE the <td> (not around it) — wrapping the
  // <td> would nest a table cell inside the tooltip's <span>, invalid HTML.
  // Disabled days still surface the tooltip because their buttons keep pointer
  // events. The hover delay keeps it quiet while the pointer crosses a
  // disabled cell on its way to a selectable one. Mirrors PickupDatePicker's
  // Day override.
  function InlineDay_({
    day,
    modifiers: _modifiers,
    children,
    ...cellProps
  }: DayProps) {
    if (!disabledHint || !isDateDisabled(fromJsDate(day.date))) {
      return createElement("td", cellProps, children);
    }
    return createElement(
      "td",
      cellProps,
      <Tooltip
        content={disabledHint}
        side="bottom"
        delay={DENSE_GRID_TOOLTIP_DELAY_MS}
      >
        {children}
      </Tooltip>,
    );
  }

  return (
    <div className="flex flex-col">
      <Calendar
        mode="single"
        fullWidth={fullWidth}
        yearNavigation={yearNavigation}
        selected={selected}
        onSelect={handleSelect}
        month={month}
        onMonthChange={setMonth}
        {...yearBounds}
        {...(hasDisabledDates
          ? { disabled: (date: Date) => isDateDisabled(fromJsDate(date)) }
          : {})}
        {...(disabledHint ? { components: { Day: InlineDay_ } } : {})}
      />
      {fullWidth && (
        // Animate the row's height (grid-rows 0fr → 1fr) and the button's
        // opacity so it eases in/out instead of snapping the dialog taller.
        <div
          className={`grid transition-[grid-template-rows] duration-200 ease-out ${
            offCurrentMonth ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="overflow-hidden">
            <button
              type="button"
              tabIndex={offCurrentMonth ? 0 : -1}
              aria-hidden={!offCurrentMonth}
              onClick={() => setMonth(toJsDate(today()))}
              className={`mx-auto mb-1 block rounded-md px-3 py-1.5 text-xs font-medium text-content-muted transition-opacity duration-200 hover:bg-muted hover:text-foreground ${
                offCurrentMonth ? "opacity-100" : "opacity-0"
              }`}
            >
              Jump to current month
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
